import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { getLocale, useT } from "@/lib/i18n";

const toLocal = (iso) => (iso ? new Date(iso).toLocaleString("sv-SE", { timeZone: "Europe/Warsaw" }).replace(" ", "T").slice(0, 16) : "");
export const fmtDeadline = (iso, locale = "pl-PL") => new Date(iso).toLocaleString(locale, { timeZone: "Europe/Warsaw", dateStyle: "long", timeStyle: "short" });

export const SignupDeadline = ({ value, onSave, label = "Termin zakończenia zapisów", tid = "admin-signup-deadline", pastText = "minął", pastClass = "text-red-400" }) => {
  const [v, setV] = useState(toLocal(value));
  useEffect(() => { setV(toLocal(value)); }, [value]);
  const expired = value && new Date(value) < new Date();
  return (
    <div className="mt-1 pt-2 border-t border-white/5 grid gap-2" data-testid={tid}>
      <div className="flex items-center gap-2 text-xs text-slate-400"><CalendarClock className="w-3.5 h-3.5" />{label} (czas polski)
        {expired && <span className={`${pastClass} font-semibold`} data-testid={`${tid}-expired`}>{pastText}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <input type="datetime-local" data-testid={`${tid}-input`} value={v} onChange={(e) => setV(e.target.value)}
          className="flex-1 min-w-[180px] h-9 px-2 rounded-lg bg-[#0B132B] border border-blue-500/20 text-sm [color-scheme:dark]" />
        <button data-testid={`${tid}-save`} disabled={!v} onClick={() => onSave(v)} className="h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold disabled:opacity-40">Zapisz</button>
        {value && <button data-testid={`${tid}-clear`} onClick={() => onSave("")} className="h-9 px-3 rounded-lg border border-white/10 text-slate-300 text-xs hover:bg-white/5">Bez terminu</button>}
      </div>
    </div>
  );
};

const parts = (ms) => [["dni", Math.floor(ms / 864e5), "cd.d"], ["godz.", Math.floor((ms % 864e5) / 36e5), "cd.h"], ["min", Math.floor((ms % 36e5) / 6e4), "cd.m"], ["sek", Math.floor((ms % 6e4) / 1e3), "cd.s"]];

export const StartCountdown = ({ iso, onStart, label: lk = "cd.opensIn", caption: ck = "cd.opensCaption", tid = "signup-countdown" }) => {
  const { t, locale } = useT();
  const label = t(lk), caption = t(ck);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = new Date(iso) - now;
  useEffect(() => { if (ms <= 0 && onStart) { onStart(); } }, [ms <= 0]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid={tid} className="fly-card p-6 sm:p-8 mt-6 text-center">
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-300">{ms > 0 ? label : t("cd.ended")}</div>
      <div className="mt-5 grid grid-cols-4 gap-2 sm:gap-4 max-w-lg mx-auto">
        {parts(Math.max(ms, 0)).map(([l, v, k]) => (
          <div key={l} className="rounded-xl bg-[#0B132B] border border-amber-500/30 py-4" data-testid={`${tid}-${l.replace(".", "")}`}>
            <div className="font-display text-4xl sm:text-5xl font-black tabular-nums">{String(v).padStart(2, "0")}</div>
            <div className="text-xs text-slate-400 mt-1">{t(k)}</div>
          </div>
        ))}
      </div>
      <p className="text-sm text-slate-300 mt-5">{caption}: <b>{fmtDeadline(iso, locale)}</b></p>
    </div>
  );
};

export const MiniCountdown = ({ start, end }) => {
  const { t } = useT();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const before = start && new Date(start) > now;
  const iso = before ? start : end;
  if (!iso || new Date(iso) <= now) return null;
  const [[, d], [, h], [, m], [, s]] = parts(new Date(iso) - now);
  const p = (x) => String(x).padStart(2, "0");
  return (
    <div data-testid="home-signup-countdown" className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-100">
      <CalendarClock className="w-3.5 h-3.5 text-amber-300" />{before ? t("cd.before") : t("cd.until")}
      <b className="font-mono tabular-nums text-amber-300">{d} {d === 1 ? t("cd.day") : t("cd.days")} {p(h)}:{p(m)}:{p(s)}</b>
    </div>
  );
};

export const DeadlineBanner = ({ iso, start }) => {
  const { t } = useT();
  if (!iso && !start) return null;
  const loc = getLocale();
  return (
    <div data-testid="signup-deadline" className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 grid gap-1">
      {start && <div className="flex flex-wrap items-center gap-2" data-testid="signup-start-info"><CalendarClock className="w-4 h-4 text-amber-300" />{t("cd.opensCaption")}: <b>{fmtDeadline(start, loc)}</b></div>}
      {iso && <div className="flex flex-wrap items-center gap-2" data-testid="signup-deadline-info"><CalendarClock className="w-4 h-4 text-amber-300" />{t("cd.endsCaption")}: <b>{fmtDeadline(iso, loc)}</b></div>}
    </div>
  );
};
